import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Carousel from "../../components/ui/Carousel";
import MediaCard from "../../components/media/MediaCard";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import Spinner from "../../components/ui/Spinner";
import useApiQuery from "../../hooks/useApiQuery";
import endpoints from "../../api/endpoints";
import { mergeCatalog } from "../../lib/catalog";
import userImage from "../../images/default-user-image.png";

const DEBOUNCE_MS = 500;
const MAX_RESULTS = 100;

const chevrons = {
  renderNext: () => <FiChevronRight size={45} />,
  renderPrev: () => <FiChevronLeft size={45} />,
};

const SearchPage = () => {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");

  // Hand-rolled debounce: the original used lodash, which is not in
  // package.json and only resolved through a hoisted transitive dependency.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const { data, loading, error, refetch } = useApiQuery(
    query ? endpoints.search(query) : null
  );

  const items = data ? mergeCatalog(data).slice(0, MAX_RESULTS) : [];
  const actors = (data?.actor ?? []).slice(0, MAX_RESULTS);
  const nothingFound =
    query && !loading && !error && !items.length && !actors.length;

  return (
    <>
      <div className="flex w-full justify-center">
        <input
          type="search"
          className="mt-[5rem] h-[40px] w-[20.4rem] rounded bg-[#37383e] p-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand md:w-[30rem]"
          placeholder="فیلم ,سریال, بازیگر"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          autoFocus
        />
      </div>

      {loading ? (
        <Spinner className="h-[40rem]" />
      ) : (
        <div className="pb-20 pr-1 sm:mt-10 sm:pr-5">
          {error && <ErrorState onRetry={refetch} />}

          {items.length > 0 && (
            <div className="relative mt-4 h-[250px] w-full sm:h-[300px] md:h-[390px]">
              <h2 className="mb-5 mr-1 mt-5 font-bold text-white">فیلم ها</h2>
              <Carousel {...chevrons}>
                {items.map((item) => (
                  <MediaCard key={`${item.type}-${item.id}`} item={item} />
                ))}
              </Carousel>
            </div>
          )}

          {actors.length > 0 && (
            <>
              <h3 className="mx-4 mt-[4rem] text-xl font-semibold leading-9">
                عوامل
              </h3>
              <div className="my-6 w-full px-6">
                <Carousel arrowClassName="h-full" {...chevrons}>
                  {actors.map((actor) => (
                    <div
                      key={actor.id}
                      className="inline-block h-[170px] align-top"
                    >
                      <Link to={`/Actor/${actor.id}`}>
                        <div className="mr-4 flex flex-col items-center">
                          <img
                            className="h-[130px] w-[130px] rounded-full object-cover"
                            src={actor.image || userImage}
                            alt={actor.name}
                            loading="lazy"
                          />
                          <span className="mt-3 text-sm font-semibold leading-5 text-white">
                            {actor.name}
                          </span>
                        </div>
                      </Link>
                    </div>
                  ))}
                </Carousel>
              </div>
            </>
          )}

          {nothingFound && <EmptyState message="نتیجه‌ای یافت نشد." />}
          {!query && <div className="min-h-[300px]" />}
        </div>
      )}
    </>
  );
};

export default SearchPage;
